/**
 * Risk band thresholds — single source of truth.
 *
 * Both the SQL aggregation (in `functions/api/_lib/queries.ts`) and the
 * UI badges/colours read from these constants so there's no drift.
 *
 * Bands:
 *   Low        : 0–29
 *   Watch      : 30–59
 *   Suspicious : 60–79
 *   High       : 80–100
 */

export type RiskBand = "low" | "watch" | "suspicious" | "high";

export interface RiskBandDef {
  key: RiskBand;
  label: string;
  min: number;
  max: number; // inclusive
  /** CSS variable name for the severity colour. */
  cssVar: `--sev-${string}`;
  /** Tailwind colour key from tailwind.config (without `text-`/`bg-`). */
  tw: "low" | "watch" | "suspicious" | "high";
}

export const RISK_BANDS: readonly RiskBandDef[] = [
  { key: "low",        label: "Low",        min: 0,  max: 29,  cssVar: "--sev-low",        tw: "low" },
  { key: "watch",      label: "Watch",      min: 30, max: 59,  cssVar: "--sev-watch",      tw: "watch" },
  { key: "suspicious", label: "Suspicious", min: 60, max: 79,  cssVar: "--sev-suspicious", tw: "suspicious" },
  { key: "high",       label: "High",       min: 80, max: 100, cssVar: "--sev-high",       tw: "high" },
] as const;

const BY_KEY: Readonly<Record<RiskBand, RiskBandDef>> = Object.fromEntries(
  RISK_BANDS.map((b) => [b.key, b]),
) as Readonly<Record<RiskBand, RiskBandDef>>;

/** Resolve a numeric score (0–100) to a band. Out-of-range clamps. */
export function bandFor(score: number): RiskBandDef {
  if (!Number.isFinite(score)) return BY_KEY.low;
  const s = Math.max(0, Math.min(100, score));
  for (const b of RISK_BANDS) {
    if (s >= b.min && s <= b.max) return b;
  }
  return BY_KEY.high;
}

export function bandByKey(k: RiskBand): RiskBandDef {
  return BY_KEY[k];
}

/**
 * SQL `multiIf` snippet that classifies `double1` (the score) into a
 * band string. Used by `_lib/queries.ts` to keep API and UI in sync.
 */
export function bandSql(scoreExpr = "double1"): string {
  return [
    `multiIf(`,
    ...RISK_BANDS.slice(0, -1).map(
      (b) => `  ${scoreExpr} <= ${b.max}, '${b.key}',`,
    ),
    `  '${RISK_BANDS[RISK_BANDS.length - 1].key}'`,
    `)`,
  ].join("\n");
}

/** "high" is the only band that triggers the "High-risk %" KPI. */
export const HIGH_RISK_THRESHOLD = BY_KEY.high.min; // 80
export const SUSPICIOUS_THRESHOLD = BY_KEY.suspicious.min; // 60
